import { useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "wouter";
import SiteLayout from "@/components/SiteLayout";
import { applySeo } from "@/lib/seo";
import {
  setJsonLd,
  organizationSchema,
  websiteSchema,
  buildFaqSchema,
} from "@/lib/schema";
import {
  MarketingHero,
  JumpLinks,
  MarketingSection,
  MarketingSectionHeader,
  MarketingCTA,
  type JumpLink,
} from "@/components/marketing";
import RelatedInsights from "@/components/RelatedInsights";
import MultisigThresholdDiagram from "@/components/marketing/MultisigThresholdDiagram";
import {
  Shield,
  Users,
  FileText,
  Key,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  XCircle,
  Phone,
  Video,
  ClipboardCheck,
  ArrowRight,
  Smartphone,
  UserX,
  Scale,
  Radar,
} from "lucide-react";

const jumpLinks: JumpLink[] = [
  { label: "What you get", href: "#what-you-get" },
  { label: "Threat model", href: "#threat-model" },
  { label: "Controls", href: "#controls" },
  { label: "Review scope", href: "#review-scope" },
  { label: "Common setups", href: "#common-setups" },
  { label: "Verification", href: "#verification" },
  { label: "Policy Pack", href: "#policy-pack" },
  { label: "Typical engagement", href: "#typical-engagement" },
  { label: "FAQs", href: "#security-faq" },
];

const threats = [
  {
    icon: Key,
    title: "Loss of keys",
    description:
      "Hardware failure, forgotten passwords, lost seed phrases, single points of failure that lock you out forever.",
  },
  {
    icon: UserX,
    title: "Coercion",
    description:
      "Physical threats, social engineering, or duress from bad actors trying to force access to your crypto.",
  },
  {
    icon: Smartphone,
    title: "Device failure",
    description:
      "Hardware wallets break, phones get lost, computers crash. Without backups, access is gone.",
  },
  {
    icon: AlertTriangle,
    title: "Social engineering",
    description:
      "Phishing, impersonation, and manipulation targeting you or your family to gain access.",
  },
  {
    icon: Scale,
    title: "Executor confusion",
    description:
      "Family members who don't understand crypto, or executors who can't find or access assets.",
  },
];

const controls = [
  {
    icon: Key,
    title: "Multisig options",
    description:
      "Multiple keys required to move funds. No single person (including Bitzo) can access assets alone.",
  },
  {
    icon: Users,
    title: "Separation of duties",
    description:
      "Different people hold different keys or roles. Reduces risk from any single compromise.",
  },
  {
    icon: Shield,
    title: "Verified contacts",
    description:
      "Every trusted contact undergoes identity and authority verification before they can act.",
  },
  {
    icon: Phone,
    title: "Rotating on-call code",
    description:
      "Time-limited access codes for verification calls, ensuring requests are genuine and timely.",
  },
  {
    icon: ClipboardCheck,
    title: "Audit logs",
    description:
      "Every action is logged with timestamps, evidence, and operator notes for full accountability.",
  },
];

const reviewScope = [
  {
    icon: Smartphone,
    title: "Device hygiene",
    description:
      "The devices your wallets touch, hardware wallets, phones, laptops, how they are stored, updated, and separated from day-to-day use. We document what exists and where the weak points sit.",
  },
  {
    icon: Key,
    title: "Wallet architecture",
    description:
      "Single-sig or multisig, hardware, exchange, or cold storage. We map how your wallets are structured and where a single device, location, or person could compromise the position.",
  },
  {
    icon: Users,
    title: "Exposure mapping",
    description:
      "Who knows your crypto exists, who knows where it is held, and who could piece it together. Reduced visibility is a control in itself, we record who knows what, and whether that is deliberate.",
  },
  {
    icon: ClipboardCheck,
    title: "Backup integrity",
    description:
      "Whether your backups would actually work: where recovery material is kept, whether it is complete, and whether recovery has ever been rehearsed. We never see or handle the backups themselves.",
  },
];

const commonSetups = [
  {
    title: "Everything on one hardware wallet",
    description:
      "One device, one seed phrase, and often only one person who knows the assets exist. If the device fails or something happens to you, everything depends on a single backup that no one has tested. It is the most common starting point we see, and the most fragile.",
  },
  {
    title: "Documented single-sig with verified contacts",
    description:
      "The same wallet, but no longer a dead end. A Policy Pack documents the recovery path in plain language, and identity-verified trusted contacts know how to coordinate if the worst happens. For many holdings this is the right trade-off: simpler than multisig, but recoverable.",
  },
  {
    title: "Compartmentalised wallets",
    description:
      "Holdings split across multiple wallets so no single recovery path exposes everything. Day-to-day funds stay easy to reach while long-term holdings sit behind stricter controls, with each compartment documented separately in the plan.",
  },
  {
    title: "Multisig with role-based signers",
    description:
      "Multiple keys, held by different people or in different places, required before funds can move. No single device, location, or person can compromise the position, and recovery can still happen when required, because signer roles and workflows are documented.",
  },
];

const engagementSteps = [
  {
    title: "Initial call",
    description:
      "A short discovery call to understand your current setup, the shape of your holdings, and who you want involved, family, executors, advisers. No commitment, and no requests for sensitive details.",
  },
  {
    title: "Inventory documentation",
    description:
      "We document what exists at the level your plan needs: which wallets, what type, how each is accessed, and who currently knows about them. Public key material only, never seed phrases or private keys.",
  },
  {
    title: "Architecture review",
    description:
      "We map wallets, devices, and access paths, and identify single points of failure. If changes make sense, you make them yourself, Bitzo coordinates and documents; we never handle devices or keys.",
  },
  {
    title: "Policy Pack issued",
    description:
      "Your plain-language recovery reference is produced as a versioned document: wallet policies and signing rules, trusted contacts and roles, recovery conditions, and verification requirements.",
  },
  {
    title: "Contacts verified",
    description:
      "Trusted contacts complete identity verification, recorded calls, rotating codes, and email ownership confirmation, before they can ever act under the plan.",
  },
  {
    title: "Periodic reviews",
    description:
      "Automated prompts every 4 to 6 months confirm details remain current, with scheduled reviews included in your plan so the Policy Pack keeps pace with your wallets, contacts, and wishes.",
  },
];

const verificationChecks = [
  "Government-issued ID verification",
  "Liveness check (video call)",
  "Consent confirmation",
  "Role and authority validation",
  "Recorded call with specialist",
];

const policyPackIncludes = [
  "Wallet policies and signing rules",
  "Public key material only (xpubs, descriptors)",
  "Trusted contact list with roles",
  "Verification requirements per contact",
  "Recovery conditions and triggers",
  "Annual review schedule",
];

const ongoingServices = [
  {
    icon: RefreshCw,
    title: "Annual reviews",
    description:
      "Yearly check-ins to update contacts, review policies, and ensure your setup reflects current wishes.",
  },
  {
    icon: Users,
    title: "Contact refresh",
    description:
      "Re-verification of trusted contacts to ensure information is current and contacts are reachable.",
  },
  {
    icon: FileText,
    title: "Playbook updates",
    description:
      "As your holdings evolve, we update your Policy Pack and operational procedures accordingly.",
  },
  {
    icon: Radar,
    title: "Watchtower monitoring",
    description:
      "Watch-only address monitoring from our own Bitcoin node, an instant email the moment coins move, without ever touching keys. Included with Continuity Plus and Private Client.",
  },
];

export default function Security() {
  useEffect(() => {
    applySeo({
      title: "Crypto Security (Non-Custodial) | Bitzo UK",
      description:
        "Non-custodial crypto security for private clients: multi-key architecture, reduced visibility, and recovery readiness. Bitzo never holds keys.",
      path: "/security",
    });
    setJsonLd("jsonld-org", organizationSchema);
    setJsonLd("jsonld-website", websiteSchema);
    setJsonLd(
      "jsonld-faq-security",
      buildFaqSchema([
        {
          question: "What does non-custodial mean?",
          answer:
            "Bitzo does not hold or control your crypto. You keep control of keys and wallets; we provide structure, verification, documentation and coordination.",
        },
        {
          question: "Do you support multisig?",
          answer:
            "Yes. Where appropriate, we help design multi-key arrangements so no single person can move funds alone, and recovery can still happen when required.",
        },
        {
          question: "Will Bitzo ever ask for seed phrases or private keys?",
          answer:
            "No. Bitzo never asks for seed phrases, private keys, wallet PINs, or exchange passwords.",
        },
        {
          question: "What happens if I lose a device or hardware wallet?",
          answer:
            "Your plan is designed for recovery readiness. With verified contacts and documented steps, access can be restored without relying on one device.",
        },
        {
          question: "Is this financial, legal, or tax advice?",
          answer:
            "No. Bitzo provides operational coordination for security and inheritance readiness. We recommend speaking to appropriate professionals for advice.",
        },
      ])
    );
  }, []);

  return (
    <SiteLayout>
      <div className="w-full bg-background relative overflow-hidden">
        <MarketingHero
          mobileImage="/assets/images/security_hero_mobile.webp"
          desktopImage="/assets/images/security_hero.webp"
          badge={{ icon: Shield, text: "Security Planning" }}
          title={
            <>
              Crypto security that works
              <br />
              <span className="text-teal-400">in the real world.</span>
            </>
          }
          subtitle="Crypto security isn't just about technology, it's about people, processes, and preparation. Bitzo helps you build defences that hold up when things go wrong."
          testId="heading-security"
          priority
        >
          <MarketingCTA>
            <Link href="/setup">
              <Button
                size="lg"
                className="bg-teal-600 hover:bg-teal-500"
                data-testid="button-choose-setup"
              >
                Choose Your Setup
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link href="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-slate-600"
                data-testid="button-speak-specialist"
              >
                Speak to a Specialist
              </Button>
            </Link>
          </MarketingCTA>
          <div className="mt-6">
            <JumpLinks links={jumpLinks} delay={0.4} />
          </div>
        </MarketingHero>

        <MarketingSection id="what-you-get" maxWidth="5xl">
          <MarketingSectionHeader
            title="What you get"
            subtitle="A documented, non-custodial security plan designed to hold up under real-world pressure."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-slate-900/50 border-slate-700/50">
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-400 mb-4">
                  <Key size={20} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Multisig or separation-of-duties options
                </h3>
                <p className="text-sm text-muted-foreground">
                  Multiple keys or roles required, no single point of failure.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-700/50">
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-400 mb-4">
                  <Users size={20} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Verified contacts and authority checks
                </h3>
                <p className="text-sm text-muted-foreground">
                  Every contact is verified before they can take action.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-700/50">
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-400 mb-4">
                  <FileText size={20} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Policy Pack with public key material only
                </h3>
                <p className="text-sm text-muted-foreground">
                  No private keys, only xpubs and descriptors are stored.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-700/50">
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-400 mb-4">
                  <ClipboardCheck size={20} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Audit trail and time-limited access
                </h3>
                <p className="text-sm text-muted-foreground">
                  Every action is logged with timestamps and evidence.
                </p>
              </CardContent>
            </Card>
          </div>
        </MarketingSection>

        <MarketingSection id="threat-model" maxWidth="5xl" background="muted">
          <MarketingSectionHeader
            title="Threat model"
            subtitle="Understanding the risks is the first step to protecting against them. These are the real-world threats that catch people out."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {threats.map((threat, i) => (
              <motion.div
                key={threat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: 0.06 * i }}
              >
                <Card className="bg-slate-900/50 border-slate-700/50 h-full">
                  <CardContent className="pt-6">
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center text-red-400 mb-4">
                      <threat.icon size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {threat.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {threat.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </MarketingSection>

        <MarketingSection id="controls" maxWidth="5xl">
          <MarketingSectionHeader
            title="Controls"
            subtitle="Layered defences that reduce risk and ensure proper verification before any action is taken."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {controls.map((control, i) => (
              <motion.div
                key={control.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: 0.06 * i }}
              >
                <Card className="bg-slate-900/50 border-slate-700/50 h-full">
                  <CardContent className="pt-6">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-400 mb-4">
                      <control.icon size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {control.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {control.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </MarketingSection>

        <MarketingSection id="review-scope" maxWidth="5xl" background="muted">
          <MarketingSectionHeader
            title="What a security review actually covers"
            subtitle="A structured look at how your crypto is actually held, recorded as plain-language documentation your family and advisers can rely on, coordination, not financial advice."
          />
          <div className="grid md:grid-cols-2 gap-6">
            {reviewScope.map((area, i) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: 0.06 * i }}
              >
                <Card className="bg-slate-900/50 border-slate-700/50 h-full">
                  <CardContent className="pt-6">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-400 mb-4">
                      <area.icon size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {area.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {area.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="text-muted-foreground text-center mt-8 max-w-3xl mx-auto">
            The output is documentation and coordination, not instructions to buy or
            sell anything. We map what exists, where the single points of failure sit,
            and what your family or executor would actually need, then we coordinate
            the changes you choose to make. Bitzo never handles devices, backups, or
            keys, and never provides regulated financial, legal, or tax advice.
          </p>
        </MarketingSection>

        <MarketingSection id="common-setups" maxWidth="4xl">
          <MultisigThresholdDiagram />
          <MarketingSectionHeader
            title="Common setups we see"
            subtitle="Most clients arrive somewhere on this progression. None of these stages is wrong, the right architecture depends on your holdings, your trust network, and how active you are."
          />
          <div className="space-y-4">
            {commonSetups.map((setup, i) => (
              <motion.div
                key={setup.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: 0.06 * i }}
              >
                <Card className="bg-slate-900/50 border-slate-700/50">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-400 font-semibold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {setup.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {setup.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="text-muted-foreground text-center mt-8 max-w-3xl mx-auto">
            Multisig is not always the right answer. It is one tool, and sometimes a
            documented single-signer setup with verified contacts is the better
            trade-off. A review tells you where you are on this progression and what, if anything, is worth changing.
          </p>
        </MarketingSection>

        <MarketingSection id="data-protection" maxWidth="4xl">
          <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-8" data-testid="section-data-protection">
            <h2 className="text-2xl font-bold text-white mb-3">How we protect your information</h2>
            <p className="text-muted-foreground leading-relaxed">
              All traffic to Bitzo is served over encrypted HTTPS. Because Bitzo is non-custodial, your seed phrases and
              private keys are never held by us, the highest-value secrets simply aren't ours to lose. Access to client
              records is restricted, every action on a recovery case is written to an audit trail, and our infrastructure
              and client database are hosted in the UK. We have not yet completed an independent security certification such
              as ISO 27001; as an early-stage company we would rather say so plainly than imply otherwise.
            </p>
          </div>
        </MarketingSection>

        <MarketingSection id="verification" maxWidth="5xl" background="muted">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Video className="w-5 h-5 text-teal-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">Strict verification</h2>
              </div>
              <p className="text-muted-foreground mb-6">
                Before anyone can take action on your behalf, they must pass our verification process.
                We don't guarantee outcomes, but we ensure every step is documented and auditable.
              </p>
              <ul className="space-y-3">
                {verificationChecks.map((check) => (
                  <li key={check} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{check}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-teal-400" />
                </div>
                <h2 id="policy-pack" className="text-2xl font-bold text-white scroll-mt-24">
                  Policy Pack
                </h2>
              </div>
              <p className="text-muted-foreground mb-6">
                Your Policy Pack documents everything needed for recovery, without storing
                any private keys or seed phrases. Only public material is included.
              </p>
              <ul className="space-y-3">
                {policyPackIncludes.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </MarketingSection>

        <MarketingSection id="typical-engagement" maxWidth="4xl">
          <MarketingSectionHeader
            title="A typical engagement"
            subtitle="Every situation is different, but a typical private client engagement looks like this. It is an illustration of the process, not a fixed script."
          />
          <div className="space-y-4">
            {engagementSteps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: 0.06 * i }}
              >
                <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
                  <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-400 font-semibold flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-muted-foreground text-center mt-8 max-w-3xl mx-auto">
            From there the plan stays live: your Policy Pack is updated as things
            change, and every verification and update is captured in the audit trail.{" "}
            <Link href="/pricing" className="underline hover:text-white">
              See what each plan includes →
            </Link>
          </p>
        </MarketingSection>

        <MarketingSection id="ongoing" maxWidth="5xl">
          <MarketingSectionHeader
            title="Ongoing protection"
            subtitle="Security isn't a one-time setup. We help you maintain your defences as circumstances change."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ongoingServices.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: 0.06 * i }}
              >
                <Card className="bg-slate-900/50 border-slate-700/50 h-full">
                  <CardContent className="pt-6">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-400 mb-4">
                      <service.icon size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </MarketingSection>

        <MarketingSection maxWidth="5xl" background="muted">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 rounded-xl bg-red-500/5 border border-red-500/20">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                We will never ask for
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <XCircle className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">Seed phrases or recovery words</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">Private keys</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">Wallet PINs or passwords</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">Exchange login credentials</span>
                </li>
              </ul>
            </div>

            <div className="p-8 rounded-xl bg-teal-500/5 border border-teal-500/20">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-400" />
                Our commitment
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">Non-custodial, we never hold your assets</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">UK-based team with phone support</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">Full audit trail for every action</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">Strict verification before any recovery</span>
                </li>
              </ul>
            </div>
          </div>
        </MarketingSection>

        <MarketingSection id="security-faq" maxWidth="3xl" background="muted">
          <MarketingSectionHeader title="Security FAQs" />
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                What does non-custodial mean?
              </h3>
              <p className="text-muted-foreground">
                Bitzo does not hold or control your crypto. You keep control of keys and wallets; we provide structure, verification, documentation and coordination.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Do you support multisig?</h3>
              <p className="text-muted-foreground">
                Yes. Where appropriate, we help design multi-key arrangements so no single person can move funds alone, and recovery can still happen when required.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Will Bitzo ever ask for seed phrases or private keys?
              </h3>
              <p className="text-muted-foreground">
                No. Bitzo never asks for seed phrases, private keys, wallet PINs, or exchange passwords.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                What happens if I lose a device or hardware wallet?
              </h3>
              <p className="text-muted-foreground">
                Your plan is designed for recovery readiness. With verified contacts and documented steps, access can be restored without relying on one device.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Is this financial, legal, or tax advice?
              </h3>
              <p className="text-muted-foreground">
                No. Bitzo provides operational coordination for security and inheritance readiness. We recommend speaking to appropriate professionals for advice.{" "}
                <Link href="/inheritance" className="underline hover:text-white">
                  See crypto inheritance planning →
                </Link>
              </p>
            </div>
          </div>
        </MarketingSection>

        <MarketingSection maxWidth="3xl">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to build your defences?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Choose a setup that matches your holdings, or speak to a specialist
              for guidance on the best approach for your situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/setup">
                <Button
                  size="lg"
                  className="bg-teal-600 hover:bg-teal-500"
                  data-testid="button-choose-setup-cta"
                >
                  Choose Your Setup
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link href="/how-it-works">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-600"
                  data-testid="button-how-it-works"
                >
                  How It Works
                </Button>
              </Link>
            </div>
          </div>
        </MarketingSection>

        <RelatedInsights title="Related Insights" />
      </div>
    </SiteLayout>
  );
}